import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useUnifiedGameForm } from '@/hooks/useUnifiedGameForm';
import { useGameRealTimeUpdates } from '@/hooks/useGameRealTimeUpdates';
import { VenueSection } from './sections/VenueSection';
import { TeamOwnerSection } from './sections/TeamOwnerSection';
import { CoachSection } from './sections/CoachSection';
import { PlayerSection } from './sections/PlayerSection';
import { ScorekeeperSection } from './sections/ScorekeeperSection';
import { ApprovalSection } from './sections/ApprovalSection';
import { AutomatedChecksInterface } from './interfaces/AutomatedChecksInterface';
import { RoleBasedSectionWrapper } from './components/RoleBasedSectionWrapper';
import { CoachDashboard } from './components/CoachDashboard';
import { SectionColorLegend } from './components/SectionColorLegend';
import { RoleInstructions } from './components/RoleInstructions';
import { RoleProgressStepper } from './components/RoleProgressStepper';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, CheckCircle, Clock, AlertCircle, FileQuestion, RefreshCw } from 'lucide-react';
import { formatGameDateTime } from '@/utils/timezone';
import { calculateGameStatus, getGameStatusInfo } from '@/utils/gameStatusUtils';
import { ExpiredFormBanner } from './components/ExpiredFormBanner';
import { CountdownTimer } from './components/CountdownTimer';
import { useNavigate } from 'react-router-dom';

// Helper function to format game date and time with timezone support
const formatGameDisplayData = (gameData: any) => {
  if (!gameData?.game_date || !gameData?.game_time) {
    return {
      date: 'TBD',
      time: 'TBD',
      fullDateTime: 'TBD'
    };
  }
  
  try {
    const formatted = formatGameDateTime(
      gameData.game_date,
      gameData.game_time,
      gameData.game_timezone || 'UTC'
    );
    
    return {
      date: formatted.date,
      time: formatted.timeWithZone,
      fullDateTime: formatted.fullDateTime
    };
  } catch (error) {
    console.error('Error formatting game date/time:', error);
    return {
      date: gameData.game_date || 'TBD',
      time: gameData.game_time || 'TBD',
      fullDateTime: `${gameData.game_date || 'TBD'} ${gameData.game_time || 'TBD'}`
    };
  }
};

interface UnifiedGameFormProps {
  gameId: string;
  gameType: 'regular' | 'venue';
}

export const UnifiedGameForm: React.FC<UnifiedGameFormProps> = ({ gameId, gameType }) => {
  const navigate = useNavigate();
  const {
    gameData,
    formData,
    loading,
    saving,
    canAccessSection,
    saveSectionData,
    submitForApproval,
    userRole,
    refetch
  } = useUnifiedGameForm({ gameId, gameType });

  // Get user team information
  const [userTeamInfo, setUserTeamInfo] = React.useState<{
    teamId?: number;
    teamSide?: 'home' | 'away';
  }>({});

  const [focusedSection, setFocusedSection] = React.useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [readyPlayersCount, setReadyPlayersCount] = React.useState<{ home: number; away: number }>({ home: 0, away: 0 });

  // Fetch ready player counts
  React.useEffect(() => {
    const fetchReadyPlayers = async () => {
      if (!gameData) return;
      try {
        const homeReady = (gameData as any).home_ready_players?.length || 0;
        const awayReady = (gameData as any).away_ready_players?.length || 0;
        setReadyPlayersCount({ home: homeReady, away: awayReady });
      } catch (e) {
        console.error('Error counting ready players:', e);
      }
    };
    fetchReadyPlayers();
  }, [gameData]);

  // Handle real-time updates
  const handleTeamChange = React.useCallback(async () => {
    console.log('Team change detected, refreshing form data...');
    setIsRefreshing(true);
    
    // Wait a bit for the database to settle
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Reload game data
    await refetch();
    
    setIsRefreshing(false);
  }, [refetch]);

  const handleGameUpdate = React.useCallback(async () => {
    console.log('Game update detected, refreshing form data...');
    setIsRefreshing(true);
    
    // Wait a bit for the database to settle
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Reload game data
    await refetch();
    
    setIsRefreshing(false);
  }, [refetch]);

  // Set up real-time updates
  useGameRealTimeUpdates({
    gameId,
    onGameUpdate: handleGameUpdate
  });

  React.useEffect(() => {
    const getUserTeamInfo = async () => {
      if (!gameData) return;

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        console.log('UnifiedGameForm: Getting user team info', {
          userId: user.id,
          homeTeamId: gameData.home_team_id,
          awayTeamId: gameData.away_team_id
        });

        // Check if user is coach or team owner for home team
        if (gameData.home_team_id) {
          const { data: homeCoach } = await supabase
            .from('team_coaches')
            .select('team_id')
            .eq('team_id', gameData.home_team_id)
            .eq('coach_id', user.id)
            .maybeSingle();

          const { data: homeOwner } = await supabase
            .from('teams')
            .select('team_id')
            .eq('team_id', gameData.home_team_id)
            .eq('created_by', user.id)
            .maybeSingle();

          console.log('Home team check:', { homeCoach, homeOwner });

          if (homeCoach || homeOwner) {
            const teamInfo = { teamId: gameData.home_team_id, teamSide: 'home' as const };
            console.log('Setting user team info (home):', teamInfo);
            setUserTeamInfo(teamInfo);
            return;
          }
        }

        // Check if user is coach or team owner for away team
        if (gameData.away_team_id) {
          const { data: awayCoach } = await supabase
            .from('team_coaches')
            .select('team_id')
            .eq('team_id', gameData.away_team_id)
            .eq('coach_id', user.id)
            .maybeSingle();

          const { data: awayOwner } = await supabase
            .from('teams')
            .select('team_id')
            .eq('team_id', gameData.away_team_id)
            .eq('created_by', user.id)
            .maybeSingle();

          console.log('Away team check:', { awayCoach, awayOwner });

          if (awayCoach || awayOwner) {
            const teamInfo = { teamId: gameData.away_team_id, teamSide: 'away' as const };
            console.log('Setting user team info (away):', teamInfo);
            setUserTeamInfo(teamInfo);
            return;
          }
        }

        console.log('No team association found for user');
      } catch (error) {
        console.error('Error getting user team info:', error);
      }
    };

    getUserTeamInfo();
  }, [gameData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading game form...</span>
      </div>
    );
  }

  if (!gameData || !formData) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive" />
            <div>
              <h3 className="text-lg font-semibold mb-2">Game Not Found</h3>
              <p className="text-muted-foreground mb-4">
                The game with ID {gameId} could not be found.
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                This might happen if:
                • The game code is incorrect
                • The game hasn't been created yet
                • The game was deleted
              </p>
              <div className="space-y-2">
                <Button 
                  onClick={() => navigate('/create-game')}
                  className="w-full"
                >
                  Create New Game
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/games')}
                  className="w-full"
                >
                  View All Games
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check game expiry status
  const gameStatus = calculateGameStatus(
    gameData.game_date, 
    gameData.game_time, 
    gameData.game_status, 
    2, 
    gameData.game_timezone || 'UTC'
  );
  const gameStatusInfo = getGameStatusInfo(gameStatus);
  const isExpired = gameStatus === 'expired' || gameStatus === 'missed' || gameStatus === 'completed';

  // Show expired form banner if game is expired
  if (isExpired) {
    return (
      <div className="space-y-6">
        <ExpiredFormBanner 
          gameData={gameData}
          gameStatus={gameStatus}
          gameStatusInfo={gameStatusInfo}
        />
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return 'default' as const;
      case 'in_progress':
        return 'secondary' as const;
      default:
        return 'outline' as const;
    }
  };

  const scrollToSection = (sectionId: string) => {
    setFocusedSection(sectionId);
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => setFocusedSection(null), 2000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Section Color Legend */}
      <SectionColorLegend />
      
      {/* Role Instructions */}
      <RoleInstructions userRole={userRole} />

      {/* Countdown Timer for upcoming games */}
      {gameStatus === 'upcoming' && (
        <CountdownTimer 
          gameDate={gameData.game_date}
          gameTime={gameData.game_time}
          gameTitle={gameData.game_name || 'Game'}
        />
      )}

      {/* Coach Dashboard */}
      {userRole === 'coach' && userTeamInfo.teamId && (
        <CoachDashboard
          gameData={gameData}
          formData={formData}
          userTeamInfo={userTeamInfo}
          onSectionFocus={scrollToSection}
        />
      )}

      {/* Game Form Header */}
      <Card>
        <CardHeader className="px-4 py-5 md:px-6 md:py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="text-xl md:text-2xl flex items-center gap-2 flex-wrap">
                {(gameData as any).constructed_title || gameData.game_name || (gameData as any).game_title}
                {getStatusIcon(formData.gameFormStatus)}
                {isRefreshing && (
                  <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground ml-2" />
                )}
              </CardTitle>
              <CardDescription className="text-sm md:text-base mt-2">
                {(() => {
                  const formatted = formatGameDisplayData(gameData);
                  return `${formatted.date} at ${formatted.time}`;
                })()}
                {gameData.venue_name && ` • ${gameData.venue_name}`}
                {(gameData as any).age_group && (
                  <div className="mt-2">
                    <Badge variant="outline" className="text-xs md:text-sm px-2 py-1">
                      Age Group: {((gameData as any).age_group as string).toUpperCase()}
                    </Badge>
                  </div>
                )}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={getStatusVariant(formData.gameFormStatus)} className="text-sm md:text-base px-3 py-1.5">
                {formData.gameFormStatus.replace('_', ' ').toUpperCase()}
              </Badge>
              {gameData.game_code && (
                <Badge variant="outline" className="text-sm md:text-base px-3 py-1.5">
                  Code: {gameData.game_code}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Role-Based Progress Stepper */}
      <RoleProgressStepper
        userRole={userRole}
        formData={formData}
        gameData={gameData}
        minimumPlayersRequired={(gameData as any).minimum_players_required || 1}
        readyPlayersCount={readyPlayersCount}
        onScrollToSection={scrollToSection}
      />

      {/* Form Sections - Now with Dynamic Visibility */}
      <div className="space-y-6">
        {/* Venue Section */}
        {canAccessSection('venue', 'view') && (
          <div id="section-venue" className={focusedSection === 'venue' ? 'ring-2 ring-primary rounded-lg' : ''}>
            <RoleBasedSectionWrapper
              sectionTitle="Venue Information"
              sectionDescription="Game details, venue information, and team assignments"
              userRole={userRole}
              canEdit={canAccessSection('venue', 'edit')}
              canView={canAccessSection('venue', 'view')}
              isCompleted={formData.formSectionsCompleted?.venue}
              isRequired={true}
              onSectionAction={() => scrollToSection('venue')}
            >
              <VenueSection
                gameData={gameData}
                canEdit={canAccessSection('venue', 'edit')}
                canApprove={canAccessSection('venue', 'approve')}
                onSave={(data) => saveSectionData('venue', data)}
                saving={saving}
              />
            </RoleBasedSectionWrapper>
          </div>
        )}

        {/* Team Owner Section */}
        {canAccessSection('team_owner', 'view') && (
          <div id="section-team_owner" className={focusedSection === 'team_owner' ? 'ring-2 ring-primary rounded-lg' : ''}>
            <RoleBasedSectionWrapper
              sectionTitle="Team Management"
              sectionDescription="Manage coaches, team roster, and game strategy"
              userRole={userRole}
              canEdit={canAccessSection('team_owner', 'edit')}
              canView={canAccessSection('team_owner', 'view')}
              isCompleted={formData.formSectionsCompleted?.teams}
              isRequired={userRole === 'team-owner'}
              onSectionAction={() => scrollToSection('team_owner')}
            >
              <TeamOwnerSection
                gameData={gameData}
                canEdit={canAccessSection('team_owner', 'edit')}
                canApprove={canAccessSection('team_owner', 'approve')}
                onSave={(data) => saveSectionData('team_owner', data)}
                saving={saving}
                userTeamId={userTeamInfo.teamId}
                userTeamSide={userTeamInfo.teamSide}
              />
            </RoleBasedSectionWrapper>
          </div>
        )}

        {/* Coach Section */}
        {canAccessSection('coach', 'view') && (
          <div id="section-coach" className={focusedSection === 'coach' ? 'ring-2 ring-primary rounded-lg' : ''}>
            <RoleBasedSectionWrapper
              sectionTitle="Coach Management"
              sectionDescription="Player selection, lineup management, and team readiness"
              userRole={userRole}
              canEdit={canAccessSection('coach', 'edit')}
              canView={canAccessSection('coach', 'view')}
              isCompleted={formData.formSectionsCompleted?.teams}
              isRequired={userRole === 'coach'}
              onSectionAction={() => scrollToSection('coach')}
            >
              <CoachSection
                gameData={gameData}
                canEdit={canAccessSection('coach', 'edit')}
                canApprove={canAccessSection('coach', 'approve')}
                onSave={(data) => saveSectionData('coach', data)}
                saving={saving}
                userTeamId={userTeamInfo.teamId}
                userTeamSide={userTeamInfo.teamSide}
              />
            </RoleBasedSectionWrapper>
          </div>
        )}

        {/* Player Section */}
        {canAccessSection('player', 'view') && (
          <div id="section-player" className={focusedSection === 'player' ? 'ring-2 ring-primary rounded-lg' : ''}>
            <RoleBasedSectionWrapper
              sectionTitle="Player Status"
              sectionDescription="Update your attendance and readiness status"
              userRole={userRole}
              canEdit={canAccessSection('player', 'edit')}
              canView={canAccessSection('player', 'view')}
              isCompleted={formData.formSectionsCompleted?.players}
              isRequired={userRole === 'player'}
              onSectionAction={() => scrollToSection('player')}
            >
              <PlayerSection
                gameData={gameData}
                canEdit={canAccessSection('player', 'edit')}
                canApprove={canAccessSection('player', 'approve')}
                onSave={(data) => saveSectionData('player', data)}
                saving={saving}
              />
            </RoleBasedSectionWrapper>
          </div>
        )}

        {/* Scorekeeper Section */}
        {canAccessSection('scorekeeper', 'view') && (
          <div id="section-scorekeeper" className={focusedSection === 'scorekeeper' ? 'ring-2 ring-primary rounded-lg' : ''}>
            <RoleBasedSectionWrapper
              sectionTitle="Scorekeeper Assignment"
              sectionDescription="Assign and manage game scorekeepers"
              userRole={userRole}
              canEdit={canAccessSection('scorekeeper', 'edit')}
              canView={canAccessSection('scorekeeper', 'view')}
              isCompleted={formData.formSectionsCompleted?.scoring}
              isRequired={false}
              onSectionAction={() => scrollToSection('scorekeeper')}
            >
              <ScorekeeperSection
                gameData={gameData}
                canEdit={canAccessSection('scorekeeper', 'edit')}
                canApprove={canAccessSection('scorekeeper', 'approve')}
                onSave={(data) => saveSectionData('scorekeeper', data)}
                saving={saving}
              />
            </RoleBasedSectionWrapper>
          </div>
        )}

        {/* Approval Section */}
        {canAccessSection('approval', 'view') && (
          <div id="section-approval" className={focusedSection === 'approval' ? 'ring-2 ring-primary rounded-lg' : ''}>
            <RoleBasedSectionWrapper
              sectionTitle="Game Approval"
              sectionDescription="Review and approve game details"
              userRole={userRole}
              canEdit={canAccessSection('approval', 'edit')}
              canView={canAccessSection('approval', 'view')}
              isCompleted={formData.gameFormStatus === 'approved'}
              isRequired={userRole === 'admin' || userRole === 'venue'}
              onSectionAction={() => scrollToSection('approval')}
              requiresApprovalAction={true}
              canApprove={canAccessSection('approval', 'approve')}
            >
              <ApprovalSection
                gameData={gameData}
                formData={formData}
                canEdit={canAccessSection('approval', 'edit')}
                canApprove={canAccessSection('approval', 'approve')}
                onSubmitApproval={submitForApproval}
                saving={saving}
                userRole={userRole}
              />
            </RoleBasedSectionWrapper>
          </div>
        )}

        {/* Automated Checks Section */}
        {(userRole === 'admin' || userRole === 'venue' || userRole === 'coach') && (
          <div id="section-automated-checks">
            <RoleBasedSectionWrapper
              sectionTitle="Automated Checks"
              sectionDescription="System monitoring and forfeit detection"
              userRole={userRole}
              canEdit={false}
              canView={true}
              isCompleted={false}
              isRequired={false}
            >
              <AutomatedChecksInterface
                gameId={gameId}
                gameType={gameType}
                gameData={gameData}
                onForfeitTriggered={(reason, team) => {
                  console.log('Forfeit triggered:', reason, team);
                  // TODO: Handle forfeit logic
                }}
              />
            </RoleBasedSectionWrapper>
          </div>
        )}
      </div>
    </div>
  );
};
